<script setup lang="ts">
/**
 * ElectionMap Component
 *
 * Interactive US map showing election prediction market data.
 * Uses D3.js geoAlbersUsa projection with us-atlas TopoJSON.
 *
 * Supports two data modes:
 * 1. API-driven: Pass `stateFills` Map from useElectionMapData composable
 * 2. Legacy: Pass `elections` Record (backwards compatible)
 *
 * @see Plan: docs/plans/2026-01-27-feat-election-hub-react-to-vue-migration-plan.md
 */
import { onMounted, onBeforeUnmount, computed, ref } from 'vue';
import { media } from '@ibgroup-tws-webapps/ratio.ui';
import { useElectionMap, type StateFeature } from '../composables/useElectionMap';
import {
  FIPS_TO_STATE,
  STATE_TO_FIPS,
  STATE_NAMES,
  type USStateCode,
  type ElectionContract,
} from '../types/election.types';
import { getMapNeutralColor } from '../constants/partyColors';

// Props
const props = withDefaults(
  defineProps<{
    /** Election contracts data by state code (legacy mode) */
    elections?: Record<USStateCode, ElectionContract>;
    /** API-driven state fills: Map<FIPS, color> from useElectionMapData */
    stateFills?: Map<string, string>;
    /** Currently selected state code */
    selectedState?: USStateCode | null;
    /** Disable transitions during tab switching to prevent flash */
    isTabSwitching?: boolean;
    /** Current office tab for GTM tracking class (e.g. "governor", "senate", "house") */
    trackingOffice?: string;
    /** Current race type for GTM tracking class (e.g. "general", "democrat-primary") */
    trackingRaceType?: string;
  }>(),
  {
    elections: undefined,
    stateFills: undefined,
    selectedState: null,
    isTabSwitching: false,
    trackingOffice: '',
    trackingRaceType: '',
  },
);

// Emits
const emit = defineEmits<{
  /** Emitted when a state is clicked */
  selectState: [stateCode: USStateCode];
  /** Emitted when zoom level changes */
  'update:zoomed': [isZoomed: boolean];
}>();

// Composable for D3 map rendering
const { states, isLoading, error, loadMap, getPath, getCentroid, getStateColor, dimensions } = useElectionMap();

// Theme-aware neutral color for empty states
const neutralColor = computed(() => getMapNeutralColor(media.scheme === 'dark'));

// Load map data on mount
onMounted(() => {
  loadMap();
});

// Zoom state
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;
const zoomLevel = ref(1);
const panX = ref(0);
const panY = ref(0);

const viewBox = computed(() => {
  const scale = zoomLevel.value;
  const w = dimensions.width / scale;
  const h = dimensions.height / scale;
  const x = (dimensions.width - w) / 2 + panX.value;
  const y = (dimensions.height - h) / 2 + panY.value;
  return `${x} ${y} ${w} ${h}`;
});

const canZoomIn = computed(() => zoomLevel.value < MAX_ZOOM);
const canZoomOut = computed(() => zoomLevel.value > MIN_ZOOM);
const isZoomed = computed(() => zoomLevel.value > MIN_ZOOM);

function clampPan() {
  const scale = zoomLevel.value;
  const w = dimensions.width / scale;
  const h = dimensions.height / scale;
  const maxPanX = (dimensions.width - w) / 2;
  const maxPanY = (dimensions.height - h) / 2;
  panX.value = Math.max(-maxPanX, Math.min(maxPanX, panX.value));
  panY.value = Math.max(-maxPanY, Math.min(maxPanY, panY.value));
}

function zoomIn() {
  if (canZoomIn.value) {
    zoomLevel.value = Math.min(MAX_ZOOM, zoomLevel.value + ZOOM_STEP);
    clampPan();
    emit('update:zoomed', isZoomed.value);
  }
}

function zoomOut() {
  if (canZoomOut.value) {
    zoomLevel.value = Math.max(MIN_ZOOM, zoomLevel.value - ZOOM_STEP);
    clampPan();
    if (zoomLevel.value === MIN_ZOOM) {
      panX.value = 0;
      panY.value = 0;
    }
    emit('update:zoomed', isZoomed.value);
  }
}

// Drag-to-pan for non-touch devices
// Bypasses Vue reactivity during drag for 60fps: writes viewBox directly via
// requestAnimationFrame, then syncs back to panX/panY refs on pointer-up.
const isDragging = ref(false);
const didDrag = ref(false);
let dragStartX = 0;
let dragStartY = 0;
let dragStartPanX = 0;
let dragStartPanY = 0;
let activeSvg: SVGSVGElement | null = null;
let rafId = 0;
let livePanX = 0;
let livePanY = 0;

// Touch gesture state (pinch-to-zoom + drag-to-pan on mobile)
let touchRafId = 0;
let touchStartDistance = 0;
let touchStartZoom = 1;
let touchStartPanX = 0;
let touchStartPanY = 0;
let touchStartX = 0;
let touchStartY = 0;
let isTouchPanning = false;
let isTouchPinching = false;

const mobileLabelsRef = ref<SVGGElement | null>(null);

/**
 * Directly update mobile label opacity via DOM (bypasses Vue reactivity).
 * Called inside RAF during drag for 60fps label updates.
 */
function updateMobileLabels() {
  const g = mobileLabelsRef.value;
  if (!g) return;
  const scale = zoomLevel.value;
  const w = dimensions.width / scale;
  const h = dimensions.height / scale;
  const vx = (dimensions.width - w) / 2 + livePanX;
  const vy = (dimensions.height - h) / 2 + livePanY;
  const margin = Math.min(w, h) * 0.05;

  for (const child of g.children) {
    const cx = parseFloat(child.getAttribute('x') || '');
    const cy = parseFloat(child.getAttribute('y') || '');
    if (isNaN(cx)) continue;
    const minD = Math.min(cx - vx, vx + w - cx, cy - vy, vy + h - cy);
    const opacity = minD < 0 ? 0 : minD >= margin ? 1 : minD / margin;
    (child as SVGElement).setAttribute('opacity', String(opacity));
  }
}

function computeViewBox(px: number, py: number): string {
  const scale = zoomLevel.value;
  const w = dimensions.width / scale;
  const h = dimensions.height / scale;
  const maxPX = (dimensions.width - w) / 2;
  const maxPY = (dimensions.height - h) / 2;
  const cx = Math.max(-maxPX, Math.min(maxPX, px));
  const cy = Math.max(-maxPY, Math.min(maxPY, py));
  livePanX = cx;
  livePanY = cy;
  return `${(dimensions.width - w) / 2 + cx} ${(dimensions.height - h) / 2 + cy} ${w} ${h}`;
}

function onPointerDown(e: PointerEvent) {
  if (e.pointerType === 'touch' || !isZoomed.value) return;
  isDragging.value = true;
  didDrag.value = false;
  dragStartX = e.clientX;
  dragStartY = e.clientY;
  dragStartPanX = panX.value;
  dragStartPanY = panY.value;
  livePanX = panX.value;
  livePanY = panY.value;
  activeSvg = e.currentTarget as SVGSVGElement;
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
}

function onPointerMove(e: PointerEvent) {
  if (!activeSvg) return;
  const dx = e.clientX - dragStartX;
  const dy = e.clientY - dragStartY;
  if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
    didDrag.value = true;
  }
  const rect = activeSvg.getBoundingClientRect();
  const scale = dimensions.width / zoomLevel.value / rect.width;
  const newPanX = dragStartPanX - dx * scale;
  const newPanY = dragStartPanY - dy * scale;

  if (rafId) cancelAnimationFrame(rafId);
  const svg = activeSvg;
  rafId = requestAnimationFrame(() => {
    svg.setAttribute('viewBox', computeViewBox(newPanX, newPanY));
    updateMobileLabels();
    rafId = 0;
  });
}

function onPointerUp() {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
  // Sync final position back to reactive state (single update)
  panX.value = livePanX;
  panY.value = livePanY;
  isDragging.value = false;
  activeSvg = null;
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', onPointerUp);
}

// Touch gesture handlers (pinch-to-zoom + drag-to-pan on mobile)
function getTouchDistance(e: TouchEvent): number {
  const t0 = e.touches[0];
  const t1 = e.touches[1];
  return Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
}

function onTouchStart(e: TouchEvent) {
  if (e.touches.length === 2) {
    // Start pinch
    isTouchPinching = true;
    isTouchPanning = false;
    touchStartDistance = getTouchDistance(e);
    touchStartZoom = zoomLevel.value;
    touchStartPanX = panX.value;
    touchStartPanY = panY.value;
    livePanX = panX.value;
    livePanY = panY.value;
    activeSvg = e.currentTarget as SVGSVGElement;
  } else if (e.touches.length === 1 && isZoomed.value) {
    // Start pan (only when zoomed — let page scroll normally otherwise)
    isTouchPanning = true;
    didDrag.value = false;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    touchStartPanX = panX.value;
    touchStartPanY = panY.value;
    livePanX = panX.value;
    livePanY = panY.value;
    activeSvg = e.currentTarget as SVGSVGElement;
  }
}

function onTouchMove(e: TouchEvent) {
  if (isTouchPinching && e.touches.length >= 2) {
    e.preventDefault();
    const dist = getTouchDistance(e);
    const ratio = dist / touchStartDistance;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, touchStartZoom * ratio));

    if (touchRafId) cancelAnimationFrame(touchRafId);
    const svg = activeSvg;
    touchRafId = requestAnimationFrame(() => {
      zoomLevel.value = newZoom;
      const vb = computeViewBox(touchStartPanX, touchStartPanY);
      if (svg) svg.setAttribute('viewBox', vb);
      updateMobileLabels();
      touchRafId = 0;
    });
  } else if (isTouchPanning && e.touches.length === 1) {
    e.preventDefault();
    const dx = e.touches[0].clientX - touchStartX;
    const dy = e.touches[0].clientY - touchStartY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      didDrag.value = true;
    }

    if (!activeSvg) return;
    const rect = activeSvg.getBoundingClientRect();
    const scale = dimensions.width / zoomLevel.value / rect.width;
    const newPanX = touchStartPanX - dx * scale;
    const newPanY = touchStartPanY - dy * scale;

    if (touchRafId) cancelAnimationFrame(touchRafId);
    const svg = activeSvg;
    touchRafId = requestAnimationFrame(() => {
      const vb = computeViewBox(newPanX, newPanY);
      if (svg) svg.setAttribute('viewBox', vb);
      updateMobileLabels();
      touchRafId = 0;
    });
  }
}

function onTouchEnd(e: TouchEvent) {
  if (isTouchPinching) {
    if (touchRafId) {
      cancelAnimationFrame(touchRafId);
      touchRafId = 0;
    }
    // Sync reactive state
    panX.value = livePanX;
    panY.value = livePanY;

    // If zoomed out completely, reset
    if (zoomLevel.value <= MIN_ZOOM) {
      zoomLevel.value = MIN_ZOOM;
      panX.value = 0;
      panY.value = 0;
    }

    emit('update:zoomed', isZoomed.value);
    isTouchPinching = false;

    // If one finger remains and we're zoomed, transition to pan
    if (e.touches.length === 1 && isZoomed.value) {
      isTouchPanning = true;
      didDrag.value = false;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchStartPanX = panX.value;
      touchStartPanY = panY.value;
      livePanX = panX.value;
      livePanY = panY.value;
    } else {
      activeSvg = null;
    }
  } else if (isTouchPanning && e.touches.length === 0) {
    if (touchRafId) {
      cancelAnimationFrame(touchRafId);
      touchRafId = 0;
    }
    panX.value = livePanX;
    panY.value = livePanY;
    isTouchPanning = false;
    activeSvg = null;
  }
}

onBeforeUnmount(() => {
  if (rafId) cancelAnimationFrame(rafId);
  if (touchRafId) cancelAnimationFrame(touchRafId);
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', onPointerUp);
});

/**
 * Check if a state has election data
 */
function stateHasData(state: StateFeature): boolean {
  if (props.stateFills) {
    return props.stateFills.has(state.id);
  }
  const stateCode = FIPS_TO_STATE[state.id];
  return !!(stateCode && props.elections?.[stateCode]);
}

function getTrackingClass(stateCode: USStateCode): string {
  if (!props.trackingOffice || !stateCode) return '';
  const state = STATE_NAMES[stateCode]?.toLowerCase().replace(/\s+/g, '-') || stateCode.toLowerCase();
  return `${props.trackingOffice}-${props.trackingRaceType}-${state}`;
}

/**
 * Handle state click - convert FIPS ID to state code and emit
 */
function handleStateClick(state: StateFeature) {
  if (didDrag.value) {
    didDrag.value = false;
    return;
  }
  if (!stateHasData(state)) return;

  const stateCode = FIPS_TO_STATE[state.id];
  if (!stateCode) return;

  emit('selectState', stateCode);
}

/**
 * Get fill color for a state path
 * Supports both API-driven stateFills and legacy elections prop
 */
function getStateFill(state: StateFeature): string {
  // API-driven mode: use stateFills Map directly
  if (props.stateFills) {
    return props.stateFills.get(state.id) ?? neutralColor.value;
  }

  // Legacy mode: use elections prop with composable helper
  const stateCode = FIPS_TO_STATE[state.id];
  if (!stateCode || !props.elections) return neutralColor.value;
  return getStateColor(stateCode, props.elections, props.selectedState);
}

// Visible viewport rect derived from viewBox (used for mobile label fade)
const viewRect = computed(() => {
  const scale = zoomLevel.value;
  const w = dimensions.width / scale;
  const h = dimensions.height / scale;
  const x = (dimensions.width - w) / 2 + panX.value;
  const y = (dimensions.height - h) / 2 + panY.value;
  return { x, y, w, h };
});

/**
 * Compute label opacity based on how close a centroid is to the viewport edge.
 * Returns 0 when outside, fades in over the outer 5% of the viewport.
 */
function getLabelOpacity(state: StateFeature): number {
  const [cx, cy] = getCentroid(state);
  if (isNaN(cx)) return 0;
  const { x, y, w, h } = viewRect.value;
  const margin = Math.min(w, h) * 0.05;
  const dLeft = cx - x;
  const dRight = x + w - cx;
  const dTop = cy - y;
  const dBottom = y + h - cy;
  const minD = Math.min(dLeft, dRight, dTop, dBottom);
  if (minD < 0) return 0;
  if (minD >= margin) return 1;
  return minD / margin;
}

// Small states that are hard to click on the map — shown as buttons on the right
const SMALL_STATES: USStateCode[] = ['VT', 'NH', 'MA', 'RI', 'CT', 'NJ', 'DE', 'MD', 'HI'];
const HIDDEN_LABELS_SET = new Set<string>([...SMALL_STATES, 'DC']);

/**
 * Get fill color for a small state button by state code
 */
function getSmallStateFill(stateCode: USStateCode): string {
  const fips = STATE_TO_FIPS[stateCode];
  if (props.stateFills) {
    return props.stateFills.get(fips) ?? neutralColor.value;
  }
  if (!props.elections) return neutralColor.value;
  return getStateColor(stateCode, props.elections, props.selectedState);
}

function smallStateHasData(stateCode: USStateCode): boolean {
  const fips = STATE_TO_FIPS[stateCode];
  if (props.stateFills) {
    return props.stateFills.has(fips);
  }
  return !!props.elections?.[stateCode];
}

function handleSmallStateClick(stateCode: USStateCode) {
  if (!smallStateHasData(stateCode)) return;
  emit('selectState', stateCode);
}

defineExpose({ zoomIn, zoomOut, canZoomIn, canZoomOut, isZoomed });
</script>

<template>
  <div class="relative w-full">
    <!-- Map Container - header/legend handled by parent -->
    <div class="flex w-full justify-center">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex h-[400px] items-center justify-center">
        <div class="text-surface-400">
          <i class="ibi-loading animate-spin text-2xl" />
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="flex h-[400px] flex-col items-center justify-center gap-2 text-error">
        <i class="ibi-warning-error-circle text-2xl" />
        <span class="text-sm">Failed to load map</span>
      </div>

      <!-- SVG Map + Small State Buttons -->
      <div v-else class="w-full max-w-[1300px]">
        <!-- Desktop: side-by-side flex that wraps buttons below when space is tight -->
        <div class="!visible flex flex-wrap items-start justify-center max-md:!hidden">
          <svg
            :viewBox="viewBox"
            class="h-[intrinsic] min-w-[500px] flex-1"
            :class="isZoomed ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''"
            aria-label="US Election Map"
            @pointerdown="onPointerDown"
          >
            <g
              v-for="state in states"
              :key="state.id"
              v-memo="[
                getStateFill(state),
                selectedState === FIPS_TO_STATE[state.id],
                isTabSwitching,
                stateHasData(state),
              ]"
            >
              <title v-if="!stateHasData(state)">No race available</title>
              <path
                :d="getPath(state)"
                :fill="getStateFill(state)"
                class="stroke-white dark:stroke-black"
                :class="[
                  selectedState === FIPS_TO_STATE[state.id] ? 'stroke-[2px]' : 'stroke-[1px]',
                  { 'transition-all duration-200': !isTabSwitching },
                  stateHasData(state) ? 'cursor-pointer hover:brightness-95' : 'cursor-default',
                  getTrackingClass(FIPS_TO_STATE[state.id]),
                ]"
                :aria-label="FIPS_TO_STATE[state.id]"
                @click="handleStateClick(state)"
              />
            </g>

            <!-- State abbreviation labels (skip small states that have buttons) -->
            <template v-for="state in states" :key="`label-${state.id}`">
              <text
                v-if="!HIDDEN_LABELS_SET.has(FIPS_TO_STATE[state.id]) && !isNaN(getCentroid(state)[0])"
                :x="getCentroid(state)[0]"
                :y="getCentroid(state)[1]"
                text-anchor="middle"
                dominant-baseline="central"
                font-size="12"
                font-weight="700"
                class="pointer-events-none select-none"
                fill="white"
                stroke="black"
                stroke-width="3"
                paint-order="stroke"
              >
                {{ FIPS_TO_STATE[state.id] }}
              </text>
            </template>
          </svg>

          <!-- Zoom controls + Small state buttons: column beside map, wraps below when narrow -->
          <div class="flex shrink-0 flex-col items-center gap-1.5 pl-4 pt-4">
            <!-- Zoom controls (hidden on touch devices) -->
            <div class="!visible mb-4 flex justify-center gap-1 lg:mb-24 [@media(pointer:coarse)]:hidden">
              <button
                :disabled="!canZoomIn"
                class="flex cursor-pointer h-7 w-7 items-center justify-center rounded !border !border-black/70 bg-(--p-surface) text-sm font-semibold text-surface-700 hover:bg-(--p-surface-100) disabled:cursor-not-allowed disabled:opacity-40 dark:!border-white/70 dark:text-surface-100 dark:hover:bg-(--p-surface-700)"
                aria-label="Zoom in"
                @click="zoomIn"
              >
                +
              </button>
              <button
                :disabled="!canZoomOut"
                class="flex cursor-pointer h-7 w-7 items-center justify-center rounded !border !border-black/70 bg-(--p-surface) text-sm font-semibold text-surface-700 hover:bg-(--p-surface-100) disabled:cursor-not-allowed disabled:opacity-40 dark:!border-white/70 dark:text-surface-100 dark:hover:bg-(--p-surface-700)"
                aria-label="Zoom out"
                @click="zoomOut"
              >
                &minus;
              </button>
            </div>
            <button
              v-for="stateCode in SMALL_STATES"
              :key="stateCode"
              :style="{ backgroundColor: getSmallStateFill(stateCode) }"
              class="state-btn h-7 w-10 rounded border-0 text-[11px] font-bold leading-none text-white hover:brightness-90"
              :class="[
                smallStateHasData(stateCode) ? 'cursor-pointer' : 'cursor-default',
                selectedState === stateCode ? 'ring-2 ring-white' : '',
                getTrackingClass(stateCode),
              ]"
              :aria-label="stateCode"
              @click="handleSmallStateClick(stateCode)"
            >
              {{ stateCode }}
            </button>
          </div>
        </div>

        <!-- Mobile: SVG full width -->
        <svg
          :viewBox="viewBox"
          class="!visible h-auto w-full md:!hidden"
          :class="isZoomed ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''"
          :style="isZoomed ? 'touch-action: none' : ''"
          aria-label="US Election Map"
          @pointerdown="onPointerDown"
          @touchstart.passive="onTouchStart"
          @touchmove="onTouchMove"
          @touchend.passive="onTouchEnd"
        >
          <g
            v-for="state in states"
            :key="state.id"
            v-memo="[
              getStateFill(state),
              selectedState === FIPS_TO_STATE[state.id],
              isTabSwitching,
              stateHasData(state),
            ]"
          >
            <title v-if="!stateHasData(state)">No race available</title>
            <path
              :d="getPath(state)"
              :fill="getStateFill(state)"
              class="stroke-white dark:stroke-black"
              :class="[
                selectedState === FIPS_TO_STATE[state.id] ? 'stroke-[2px]' : 'stroke-[1px]',
                { 'transition-all duration-200': !isTabSwitching },
                stateHasData(state) ? 'cursor-pointer hover:brightness-95' : 'cursor-default',
                getTrackingClass(FIPS_TO_STATE[state.id]),
              ]"
              :aria-label="FIPS_TO_STATE[state.id]"
              @click="handleStateClick(state)"
            />
          </g>

          <!-- State labels: appear on zoom, fade at viewport edges -->
          <!-- Opacity updated directly via DOM during drag (updateMobileLabels) -->
          <g v-if="isZoomed" ref="mobileLabelsRef">
            <template v-for="state in states" :key="`m-label-${state.id}`">
              <text
                v-if="!HIDDEN_LABELS_SET.has(FIPS_TO_STATE[state.id]) && !isNaN(getCentroid(state)[0])"
                :x="getCentroid(state)[0]"
                :y="getCentroid(state)[1]"
                text-anchor="middle"
                dominant-baseline="central"
                font-size="12"
                font-weight="700"
                class="pointer-events-none select-none"
                fill="white"
                stroke="black"
                stroke-width="3"
                paint-order="stroke"
                :opacity="getLabelOpacity(state)"
              >
                {{ FIPS_TO_STATE[state.id] }}
              </text>
            </template>
          </g>
        </svg>

        <!-- Zoom controls: own row on small screens (hidden on touch devices) -->
        <div class="!visible mt-4 mb-6 flex justify-center gap-1 md:hidden [@media(pointer:coarse)]:hidden">
          <button
            :disabled="!canZoomIn"
            class="flex cursor-pointer h-7 w-7 items-center justify-center rounded !border !border-black/70 bg-(--p-surface) text-xs font-semibold text-surface-700 hover:bg-(--p-surface-100) disabled:cursor-not-allowed disabled:opacity-40 dark:!border-white/70 dark:text-surface-100 dark:hover:bg-(--p-surface-700)"
            aria-label="Zoom in"
            @click="zoomIn"
          >
            +
          </button>
          <button
            :disabled="!canZoomOut"
            class="flex cursor-pointer h-7 w-7 items-center justify-center rounded !border !border-black/70 bg-(--p-surface) text-xs font-semibold text-surface-700 hover:bg-(--p-surface-100) disabled:cursor-not-allowed disabled:opacity-40 dark:!border-white/70 dark:text-surface-100 dark:hover:bg-(--p-surface-700)"
            aria-label="Zoom out"
            @click="zoomOut"
          >
            &minus;
          </button>
        </div>
        <!-- Small state buttons: horizontal row on small screens -->
        <div class="!visible mt-1 flex flex-wrap justify-center gap-1 md:hidden">
          <button
            v-for="stateCode in SMALL_STATES"
            :key="stateCode"
            :style="{ backgroundColor: getSmallStateFill(stateCode) }"
            class="state-btn h-5 w-8 rounded border-0 text-[9px] font-bold leading-none text-white hover:brightness-90"
            :class="[
              smallStateHasData(stateCode) ? 'cursor-pointer' : 'cursor-default',
              selectedState === stateCode ? 'ring-2 ring-white' : '',
            ]"
            :aria-label="stateCode"
            @click="handleSmallStateClick(stateCode)"
          >
            {{ stateCode }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.state-btn {
  text-shadow:
    -1px -1px 0 rgb(0 0 0 / 100%),
    1px -1px 0 rgb(0 0 0 / 100%),
    -1px 1px 0 rgb(0 0 0 / 100%),
    1px 1px 0 rgb(0 0 0 / 100%);
}
</style>
<!-- last modified by AI tools on 2026-10-07 -->
